import { inject, Injectable } from '@angular/core';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { HttpParams } from '@angular/common/http';
import { PartnerOnboardingLogEvent } from '../models/partner-onboarding-logevent';
import { As2Details, DocumentTypeOutput, Identification, PartnerContact, PartnerConversation, PartnerName, PartnerOnboarding, PartnerOnboardingComment, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { lastValueFrom, map, Observable } from 'rxjs';
import { PartnerOnboardingPreset } from '../models/partner-onboarding-preset';


@Injectable({
    providedIn: 'root'
})
export class PartnerOnboardingService {
    httpService = inject(JsonHttpService);

    // Onboarding Data
    
    getPartnerOnboarding(partnerId: number): Promise<PartnerOnboarding> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        const observable = this.httpService.get('/api/partneronboarding/get-partneronboarding', options) as Observable<PartnerOnboarding>;
        return lastValueFrom(observable);
    }

    savePartnerOnboarding(partnerOnboarding: PartnerOnboarding): Promise<PartnerOnboarding> {
        const observable = this.httpService.post('/api/partneronboarding/save-partneronboarding', partnerOnboarding) as Observable<PartnerOnboarding>;
        return lastValueFrom(observable);
    }

    sendUrlToPartner(partnerOnboarding: PartnerOnboarding): Promise<unknown> {
        const observable = this.httpService.post('/api/partneronboarding/send-url-to-partner', partnerOnboarding) as Observable<unknown>;
        return lastValueFrom(observable);
    }

    logEvent(partnerOnboardingLogEvent: PartnerOnboardingLogEvent): Promise<unknown> {
        const observable = this.httpService.post('/api/partneronboarding/log-event', partnerOnboardingLogEvent) as Observable<unknown>;
        return lastValueFrom(observable);
    }

    savePartnerName(partnerId: number, partnerName: PartnerName): Promise<PartnerName> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        const observable = this.httpService.post('/api/partneronboarding/save-partnername', partnerName, options) as Observable<PartnerName>;
        return lastValueFrom(observable);
    }

    savePartnerContact(partnerId: number, partnerContact: PartnerContact): Promise<number> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        const observable = this.httpService.post('/api/partneronboarding/save-partnercontact', partnerContact, options) as Observable<number>;
        return lastValueFrom(observable);
    }

    saveIdentification(partnerId: number, identification: Identification): Promise<number> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        const observable = this.httpService.post('/api/partneronboarding/save-identification', identification, options) as Observable<number>;
        return lastValueFrom(observable);
    }

    saveDocumentType(partnerInId: number, partnerOutId: number, documentConfigInId: number, documentConfigOutId: number, documentType: DocumentTypeOutput, isInbound: boolean, transportOptions: Record<string, string>): Promise<number> {
        const options = { params: new HttpParams().set('partnerInId', `${partnerInId}`) };
        options.params = options.params.set('partnerOutId', `${partnerOutId}`);
        options.params = options.params.set('documentConfigInId', `${documentConfigInId}`);
        options.params = options.params.set('documentConfigOutId', `${documentConfigOutId}`);
        options.params = options.params.set('isInbound', `${isInbound}`);
        for (const [key, value] of Object.entries(transportOptions)) {
            options.params = options.params.set(`options[${key}]`, `${value}`);
        }
        const observable = this.httpService.post('/api/partneronboarding/save-documenttype', documentType, options) as Observable<number>;
        return lastValueFrom(observable);
    }

    getInternalPartnerId(): Promise<number> {
        const observable = this.httpService.get('/api/partneronboarding/get-internalpartnerid') as Observable<number>;
        return lastValueFrom(observable);
    }

    getPartnerOnboardingPresets(): Promise<PartnerOnboardingPreset[]> {
        const observable = this.httpService.get('/api/partneronboarding/get-partneronboardingpresets') as Observable<PartnerOnboardingPreset[]>;
        return lastValueFrom(observable);
    }

    savePartnerOnboardingPreset(preset: PartnerOnboardingPreset): Promise<PartnerOnboardingPreset> {
        const observable = this.httpService.post('/api/partneronboarding/save-partneronboardingpreset', preset) as Observable<PartnerOnboardingPreset>;
        return lastValueFrom(observable);
    }

    deletePartnerOnboardingPreset(id: number): Promise<void> {
        const options = { params: new HttpParams().set('id', `${id}`) };
        const observable = this.httpService.delete('/api/partneronboarding/delete-partneronboardingpreset', options) as Observable<void>;
        return lastValueFrom(observable);
    }

    getConversations(partnerOnboardingId: number): Promise<PartnerConversation[]> {
        const options = { params: new HttpParams().set('partnerOnboardingId', `${partnerOnboardingId}`) };
        const conversations = this.httpService.get('/api/partneronboarding/get-conversations', options) as Observable<PartnerConversation[]>;
        return lastValueFrom(conversations.pipe(
            map(result => {
                this.convertAllDateProperties(result);
                return result;
            })
        ));
    }

    postComment(comment: PartnerOnboardingComment, conversationId: string, partnerOnboardingId: number): Promise<PartnerConversation> {
        const options = { params: new HttpParams().set('conversationId', `${conversationId}`) };
        options.params = options.params.append('partnerOnboardingId', `${partnerOnboardingId}`);
        const conversation = this.httpService.post('/api/partneronboarding/post-comment', comment, options) as Observable<PartnerConversation>;
        return lastValueFrom(conversation);
    }

    setConversationReadByCustomer(conversationId: string): Promise<void> {
        const options = { params: new HttpParams().set('conversationId', `${conversationId}`) };
        const conversation = this.httpService.post('/api/partneronboarding/set-conversation-read', null, options) as Observable<void>;
        return lastValueFrom(conversation);
    }

    notifyPartnerFieldReopened(partnerOnboardingId: number, notificationMessage: string): Promise<void> {
        const options = { params: new HttpParams().set('partnerOnboardingId', `${partnerOnboardingId}`) };
        options.params = options.params.append('notificationMessage', `${notificationMessage}`);
        const conversation = this.httpService.post('/api/partneronboarding/notify-partner-field-reopened', null, options) as Observable<void>;
        return lastValueFrom(conversation);
    }

    clearTransportCredentials(partnerId: number, transportTypes: TransportType[]): Promise<void> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        const result = this.httpService.post('/api/partneronboarding/clear-transport-credentials', transportTypes, options) as Observable<void>;
        return lastValueFrom(result);
    }

    getAs2Details(): Promise<As2Details> {
        const as2Details = this.httpService.get('/api/partneronboarding/get-as2-details') as Observable<As2Details>;
        return lastValueFrom(as2Details);
    }

    private convertAllDateProperties(node: unknown): void {
        if (!node) return;

        if (Array.isArray(node)) {
            for (const item of node) this.convertAllDateProperties(item);
            return;
        }

        if (typeof node === 'object') {
            for (const key of Object.keys(node)) {
                const val = (node as Record<string, unknown>)[key];
                if (val === null) continue;

                if (typeof val === 'string' && this.isIsoDateString(val)) {  
                    node[key] = new Date(val);
                } else if (typeof val === 'object') {
                    this.convertAllDateProperties(val);
                }
            }
        }
    }

    private isIsoDateString(value: string): boolean {
        const isoDateTimeRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d+)?(Z|[+-][01]\d:[0-5]\d)?$/;
        if (!isoDateTimeRegex.test(value)) return false;
        return true;
    }
}
